import { useEffect, useState } from "react";
import { Menu, X, Lock } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDiscord } from "../../context/DiscordContext";
import DiscordButton from "./DiscordButton";
import ServerTimePill from "./ServerTimePill";

// type:"anchor" — scrolls to section on home; type:"route" — full page
// gated:true — hidden Survivor-only content; shows a lock for non-Survivors
const NAV_LINKS = [
  { id: "home", label: "Home", type: "anchor" },
  { id: "lore", label: "Lore", type: "anchor" },
  { id: "members", label: "Members", type: "route", path: "/members" },
  { id: "media", label: "Media", type: "route", path: "/media" },
  { id: "rules", label: "Rules", type: "route", path: "/rules", gated: true },
  { id: "guide", label: "Guide", type: "route", path: "/guide", gated: true },
  { id: "apply", label: "Apply", type: "route", path: "/apply" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeAnchor, setActiveAnchor] = useState("home");
  const { isConnected, canViewSurvivorContent } = useDiscord();
  const location = useLocation();
  const navigate = useNavigate();
  const onHome = location.pathname === "/";

  useEffect(() => {
    let raf = 0;
    let last = -1;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const y = window.scrollY;
        if (y === last) return;
        last = y;
        setScrolled(y > 24);
        if (!onHome) return;
        const offset = y + 120;
        const anchorIds = NAV_LINKS.filter((l) => l.type === "anchor").map((l) => l.id);
        for (let i = anchorIds.length - 1; i >= 0; i--) {
          const el = document.getElementById(anchorIds[i]);
          if (el && el.offsetTop <= offset) {
            setActiveAnchor((prev) => (prev === anchorIds[i] ? prev : anchorIds[i]));
            break;
          }
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [onHome]);

  const isActive = (link) => {
    if (link.type === "route") return location.pathname === link.path;
    return onHome && activeAnchor === link.id;
  };

  const handleAnchorClick = (e, id) => {
    if (!onHome) {
      e.preventDefault();
      navigate(`/#${id}`);
    }
  };

  const NavItem = ({ link, mobile = false }) => {
    const active = isActive(link);
    const locked = !!link.gated && !canViewSurvivorContent;
    const baseClasses = `relative px-4 py-2 font-mono-ui text-xs uppercase tracking-[0.2em] transition-colors ${
      active ? "text-[#C0A875]" : "text-[#8D9A87] hover:text-[#E2E4DF]"
    } ${mobile ? "py-3 px-2 border-b border-[#283524]/60 block" : ""}`;
    const labelNode = (
      <span className="inline-flex items-center gap-1.5">
        {link.label}
        {locked && (
          <Lock
            size={10}
            className="text-[#5C6B51]"
            data-testid={`nav-${link.id}-lock-icon`}
            aria-label="Survivors only"
          />
        )}
      </span>
    );
    if (link.type === "route") {
      return (
        <Link
          to={link.path}
          data-testid={`nav-${mobile ? "mobile-" : ""}${link.id}-link`}
          className={baseClasses}
          onClick={() => mobile && setOpen(false)}
          title={locked ? "Survivors only" : undefined}
        >
          {labelNode}
          {active && !mobile && (
            <span className="absolute left-4 right-4 -bottom-0.5 h-px bg-[#C0A875]" />
          )}
        </Link>
      );
    }
    return (
      <a
        href={`${onHome ? "" : "/"}#${link.id}`}
        onClick={(e) => {
          handleAnchorClick(e, link.id);
          if (mobile) setOpen(false);
        }}
        data-testid={`nav-${mobile ? "mobile-" : ""}${link.id}-link`}
        className={baseClasses}
      >
        {labelNode}
        {active && !mobile && (
          <span className="absolute left-4 right-4 -bottom-0.5 h-px bg-[#C0A875]" />
        )}
      </a>
    );
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled || !onHome
          ? "backdrop-blur-md bg-[#0D110C]/90 border-b border-[#283524]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-[72px] flex items-center justify-between">
        {/* Logo */}
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <div className="w-9 h-9 border border-[#5C6B51] flex items-center justify-center bg-[#161D14]">
            <span className="font-display text-[#C0A875] text-xl font-bold leading-none">W</span>
          </div>
          <div className="hidden lg:flex flex-col leading-tight">
            <span className="font-display text-[#E2E4DF] text-lg uppercase tracking-wider whitespace-nowrap">
              What's Left of Us
            </span>
            <span className="label-tag text-[10px] whitespace-nowrap">STAY QUIET. STAY ALIVE.</span>
          </div>
          <span className="hidden sm:inline lg:hidden font-display text-[#E2E4DF] text-lg uppercase tracking-wider whitespace-nowrap">
            WLOU
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1">
          {NAV_LINKS.map((link) => (
            <NavItem key={link.id} link={link} />
          ))}
        </nav>

        {/* Right side */}
        <div className="hidden md:flex items-center gap-3">
          <ServerTimePill />
          <DiscordButton compact />
        </div>

        {/* Mobile toggle */}
        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden p-2 text-[#E2E4DF] border border-[#283524]"
          onClick={() => setOpen((s) => !s)}
          aria-label="Toggle menu"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="md:hidden border-t border-[#283524] bg-[#0D110C]/95 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <NavItem key={link.id} link={link} mobile />
            ))}
            <div className="pt-4 flex flex-col gap-3">
              <ServerTimePill mobile className="self-start" />
              <DiscordButton />
            </div>
            {isConnected && (
              <span className="label-tag pt-2">CONNECTED // WHITELIST UNLOCKED</span>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
